<template>
    <CompassPanel
        class="activity-detail-panel"
        :title="panelTitle"
        :icon="panelIcon"
        :modelValue="isVisible"
        :headerActions="headerActions"
        @closed="onClosePanel"
    >
        <template #header-suffix>
            <BadgeActivityStatus
                v-if="isActivityLate && fetchingStatus === fetchingStatusEnum.success"
                :tag="{ type: 'error', label: localizedTexts.isLateLabel }"
            />
        </template>
        <!-- BODY -->
        <template #default>
            <div v-if="fetchingStatus === fetchingStatusEnum.loading">
                <ActivityDetailPanelContentLoadingState />
            </div>
            <CompassAlert
                v-else-if="fetchingStatus === fetchingStatusEnum.error"
                type="error"
                :description="localizedTexts.errorAlertLabel"
                :showClose="false"
            />
            <div v-else-if="fetchingStatus === fetchingStatusEnum.success">
                <div class="activity-detail-panel__general-information">
                    <CompassDescriptionList v-if="!isActivityPhonecall" type="alternative">
                        <CompassDescriptionListItem :label="$t.__('common.labels.title')">
                            <span class="typographies-paragraph-medium-semibold">
                                {{ activityTitle }}
                            </span>
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                    <CompassDescriptionList type="alternative">
                        <CompassDescriptionListItem
                            v-if="activityDetailData.priority >= 0"
                            :label="localizedTexts.descriptionListLabel.priority"
                        >
                            <ActivitiesPriorityIconButton
                                :isActive="activityDetailData.priority > 0"
                                :isPending="isPriorityPending"
                                :enabled="activityDetailData._embed.acl.can_be_updated"
                                dataBot="activity-detail-panel__priority-icon-button"
                                @clicked="updatePriority"
                            />
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                    <CompassDescriptionList type="alternative">
                        <CompassDescriptionListItem
                            v-if="label && label.is_active"
                            :label="localizedTexts.descriptionListLabel.label"
                        >
                            <CompassTag type="dot" color="custom" :customColor="label.color">
                                {{ label.name }}
                            </CompassTag>
                        </CompassDescriptionListItem>
                        <!-- DATE -->
                        <CompassDescriptionListItem
                            v-if="shouldDisplayDateItem"
                            :label="localizedTexts.descriptionListLabel.date"
                        >
                            <div class="activity-detail-panel__general-information-date">
                                <div class="activity-detail-panel__calendar-icon-wrapper">
                                    <CompassIcon icon="calendar-fill" size="small" />
                                </div>
                                <div
                                    v-if="shouldDisplayRangeDate"
                                    class="activity-detail-panel__general-information-date-range"
                                >
                                    <span class="typographies-paragraph-medium-regular">
                                        {{
                                            $l10n.__(
                                                'du %s',
                                                formatDate(activityDetailData.datetime_start),
                                            )
                                        }}
                                    </span>
                                    <span class="typographies-paragraph-medium-regular">
                                        {{
                                            $l10n.__(
                                                'au %s',
                                                formatDate(activityDetailData.datetime_end),
                                            )
                                        }}
                                    </span>
                                </div>
                                <span v-else>
                                    {{ formattedDueDate }}
                                </span>
                            </div>
                        </CompassDescriptionListItem>
                        <!-- CONTACTS -->
                        <CompassDescriptionListItem
                            v-if="shouldDisplayEventContacts"
                            :label="localizedTexts.descriptionListLabel.contacts"
                            class="participants-avatar-text activity-detail-panel__general-information--stacked_element"
                        >
                            <div
                                v-for="contact in assignedContactsFiltered.slice(
                                    0,
                                    maxContactsToDisplay,
                                )"
                                :key="contact.id"
                            >
                                <div v-if="contact.type === 'contact'">
                                    <CompassUser
                                        size="small"
                                        :firstname="getFirstName(contact.contactInfo)"
                                        :lastname="getLastName(contact.contactInfo)"
                                    >
                                        <CompassAvatar
                                            :initials="getInitials(contact.contactInfo)"
                                            :photo="contact.contactInfo.avatar"
                                            size="small"
                                            :colorType="getAvatarColor(contact)"
                                        />
                                    </CompassUser>
                                </div>
                                <div v-else class="typographies-paragraph-medium-semibold">
                                    {{ contact.id }}
                                </div>
                            </div>
                            <template v-if="assignedContactsFiltered.length > maxContactsToDisplay">
                                <CompassPlainButton
                                    :ref="setContactsTriggerRef"
                                    :label="labelMoreContactsBtn"
                                    type="tertiary"
                                    class="activity-detail-panel__general-information--assigned-staffs__button-more"
                                    @click="isPopoverAssigneeDisplayed = true"
                                />
                                <CompassPopover
                                    :isVisible="isPopoverAssigneeDisplayed"
                                    :triggerRef="contactsTriggerRefEl"
                                    customClass="activity-detail-panel__general-information--assigned-contacts__menu-more"
                                    placement="bottom-start"
                                    @close="isPopoverAssigneeDisplayed = false"
                                >
                                    <div
                                        v-for="contact in assignedContactsFiltered.slice(
                                            maxContactsToDisplay,
                                            assignedContactsFiltered.length,
                                        )"
                                        :key="contact.id"
                                        class="menu-more__item participants-avatar-text"
                                    >
                                        <div v-if="contact.type === 'contact'">
                                            <CompassUser
                                                size="small"
                                                :firstname="getFirstName(contact.contactInfo)"
                                                :lastname="getLastName(contact.contactInfo)"
                                                :showCaption="false"
                                            >
                                                <CompassAvatar
                                                    :initials="getInitials(contact.contactInfo)"
                                                    size="small"
                                                    :colorType="getAvatarColor(contact)"
                                                    :photo="contact.contactInfo.avatar"
                                                />
                                            </CompassUser>
                                        </div>
                                        <div v-else class="typographies-paragraph-medium-semibold">
                                            {{ contact.id }}
                                        </div>
                                    </div>
                                </CompassPopover>
                            </template>
                        </CompassDescriptionListItem>
                        <CompassDescriptionListItem
                            v-if="owner"
                            :label="$t.__('common.labels.assignee')"
                        >
                            <CompassUser
                                size="small"
                                :firstname="owner.firstname"
                                :lastname="owner.lastname"
                            >
                                <CompassAvatar
                                    :initials="`${owner.firstname[0]}${owner.lastname[0]}`"
                                    :photo="owner.avatar"
                                    size="small"
                                    :colorType="getAvatarColor(owner)"
                                    :stateBadge="getStateStaff(owner)"
                                />
                            </CompassUser>
                        </CompassDescriptionListItem>
                        <!-- STAFFS -->
                        <CompassDescriptionListItem
                            v-if="assignedStaffsFiltered.length > 0"
                            :label="$t.__('common.labels.select_other_staff')"
                            class="activity-detail-panel__general-information--assigned-staffs participants-avatar-text activity-detail-panel__general-information--stacked_element"
                        >
                            <CompassUser
                                v-for="staff in assignedStaffsFiltered.slice(0, maxStaffToDisplay)"
                                :key="staff.id"
                                size="small"
                                :firstname="staff.firstname"
                                :lastname="staff.lastname"
                            >
                                <CompassAvatar
                                    :initials="`${staff.firstname[0]}${staff.lastname[0]}`"
                                    :photo="staff.avatar"
                                    size="small"
                                    :colorType="getAvatarColor(staff)"
                                    :stateBadge="getStateStaff(staff)"
                                />
                            </CompassUser>
                            <template v-if="assignedStaffsFiltered.length > maxStaffToDisplay">
                                <CompassPlainButton
                                    :ref="setStaffsTriggerRef"
                                    :label="labelMoreStaffsBtn"
                                    type="tertiary"
                                    class="activity-detail-panel__general-information--assigned-staffs__button-more"
                                    @click="isPopoverAssigneeDisplayed = true"
                                />
                                <CompassPopover
                                    :isVisible="isPopoverAssigneeDisplayed"
                                    :triggerRef="staffsTriggerRefEl"
                                    customClass="activity-detail-panel__general-information--assigned-staffs__menu-more"
                                    placement="bottom-start"
                                    @close="isPopoverAssigneeDisplayed = false"
                                >
                                    <div
                                        v-for="staff in assignedStaffsFiltered.slice(
                                            maxStaffToDisplay,
                                            assignedStaffsFiltered.length,
                                        )"
                                        :key="staff.id"
                                        class="menu-more__item"
                                    >
                                        <CompassAvatar
                                            size="small"
                                            :photo="staff.avatar"
                                            :initials="`${staff.firstname[0]}${staff.lastname[0]}`"
                                            :colorType="getAvatarColor(staff)"
                                        />
                                        <span class="typographies-paragraph-medium-regular">
                                            {{ staff.firstname }} {{ staff.lastname }}
                                        </span>
                                    </div>
                                </CompassPopover>
                            </template>
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                    <ActivityDetailDescriptionComponent
                        v-if="shouldDisplayDescriptionEventTask"
                        :text="activityDetailData.description"
                        :label="localizedTexts.descriptionListLabel.description"
                    />
                    <template v-if="isActivityPhonecall">
                        <CompassDescriptionList
                            class="activity-detail-panel__general-information-phonecall"
                        >
                            <!-- PHONECALL DURATION-->
                            <CompassDescriptionListItem
                                v-if="activityDetailData.duration"
                                :label="localizedTexts.descriptionListLabel.phoneCall.duration"
                            >
                                {{ phoneCallDuration }}
                            </CompassDescriptionListItem>
                            <!-- PHONECALL RESULT-->
                            <CompassDescriptionListItem
                                v-if="activityDetailData.result"
                                :label="localizedTexts.descriptionListLabel.phoneCall.result"
                            >
                                {{ activityDetailData.result.name }}
                            </CompassDescriptionListItem>
                        </CompassDescriptionList>
                        <!-- PHONECALL DESCRIPTION-->
                        <ActivityDetailDescriptionComponent
                            v-if="activityDetailData.description"
                            :text="activityDetailData.description"
                            :label="$t.__('common.labels.note')"
                        />
                    </template>
                </div>

                <!-- RELATED -->
                <template v-if="!isEmpty(relatedData)">
                    <h3
                        class="typographies-heading-h3 activity-detail-panel__related-object--title"
                    >
                        {{ localizedTexts.descriptionListLabel.related }}
                    </h3>
                    <div class="activity-detail-panel__related-object">
                        <ActivityDetailLinkedContact
                            v-if="isRelatedTypeContact && relatedData.contact"
                            :data="relatedData"
                        />
                        <ActivityDetailLinkedCompany
                            v-if="isRelatedTypeCompany && relatedData.company"
                            :data="relatedData"
                            :showContactSlot="!isActivityPhonecall"
                        />
                        <ActivityDetailLinkedOpportunity
                            v-if="isRelatedTypeOpporunity && relatedData.opportunity"
                            :showContactSlot="!isActivityPhonecall"
                            :data="relatedData"
                        />
                        <ActivityDetailLinkedDoc
                            v-if="isRelatedTypeDocument && relatedData.document"
                            :data="relatedData"
                        />
                    </div>
                </template>
            </div>
        </template>

        <!-- FOOTER -->
        <template #footer>
            <ActivityPanelDetailsPanelFooter
                v-if="fetchingStatus === fetchingStatusEnum.success && canUpdateStatusOrReschedule"
                :activityDate="rescheduleDatePickerValue"
                :isDone="activityIsDone"
                :pending="isFooterPending"
                :canReschedule="!activityIsDone"
                :canBeUpdated="activityDetailData._embed.acl.can_be_updated"
                :isReschedulePending="isReschedulePending"
                :hasDropdown="!isActivityPhonecall"
                @updateStatus="updateStatus"
                @updateDueDate="updateDueDate"
            />
        </template>
    </CompassPanel>
</template>

<script>
import {
    fetchActivityDetail,
    fetchActivitiesCrmMetas,
    updateActivity,
    getRelatedBatchRequest,
} from 'api/activities/activitiesService.js';
import ActivityPanelDetailsPanelFooter from 'vcomponents/activities/detailPanel/ActivityPanelDetailsPanelFooter.vue';
import ActivityDetailPanelContentLoadingState from 'vcomponents/activities/detailPanel/ActivityDetailPanelContentLoadingState.vue';
import ActivitiesPriorityIconButton from 'vcomponents/activities/priorityIconButton/ActivitiesPriorityIconButton.vue';
import BadgeActivityStatus from 'vcomponents/utilities/badge/BadgeActivityStatus.vue';
import SalesDocumentsStatus from 'Config/authenticated/SalesDocumentsStatus';
import { randAvatarColorFullname } from 'utils/avatar';
import activitiesListingMixins from 'mixins/activities/activitiesListingMixins';
import { flatten, isEmpty } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import ActivitiesStore from 'stores/activitiesStore.js';
import Feature from 'Services/Feature';
import {
    markAsDoneWithNewForm,
    markAsTodoWithNewForm,
} from 'Helpers/activities/activitiesPhoneCallHelpers.js';
import orderStaffListByOwnerAndName from 'Helpers/orderStaffListByOwnerAndName';
import { defineAsyncComponent } from 'vue';

const { mapActions } = createNamespacedHelpers('activitiesStores');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const { mapGetters: userMapGetters } = createNamespacedHelpers('user');
const fetchingStatusEnum = Object.freeze({
    loading: 'loading',
    success: 'success',
    error: 'error',
});
const ACTIVITY_STATUS = Object.freeze({
    TODO: 'todo',
    DONE: 'done',
});
const RELATED_TYPES = Object.freeze({
    COMPANY: 'company',
    CONTACT: 'contact',
    DOCUMENT: 'document',
    OPPORTUNITY: 'opportunity',
});

export default {
    components: {
        ActivityDetailLinkedCompany: defineAsyncComponent(
            () => import('vcomponents/activities/detailPanel/ActivityDetailLinkedCompany.vue'),
        ),
        ActivityDetailLinkedContact: defineAsyncComponent(
            () => import('vcomponents/activities/detailPanel/ActivityDetailLinkedContact.vue'),
        ),
        ActivityDetailLinkedDoc: defineAsyncComponent(
            () => import('vcomponents/activities/detailPanel/ActivityDetailLinkedDoc.vue'),
        ),
        ActivityDetailLinkedOpportunity: defineAsyncComponent(
            () => import('vcomponents/activities/detailPanel/ActivityDetailLinkedOpportunity.vue'),
        ),
        ActivityDetailDescriptionComponent: defineAsyncComponent(
            () =>
                import('vcomponents/activities/detailPanel/ActivityDetailDescriptionComponent.vue'),
        ),
        ActivityPanelDetailsPanelFooter,
        ActivitiesPriorityIconButton,
        BadgeActivityStatus,
        ActivityDetailPanelContentLoadingState,
    },
    mixins: [activitiesListingMixins],
    props: {
        activityDetailDisplayed: {
            type: Object,
            default: () => {},
        },
        callback: {
            type: Function,
            default: null,
        },
        callbackOnDelete: {
            type: Function,
            default: null,
        },
        callbackOnCreate: {
            type: Function,
            default: null,
        },
        shouldFetchAdditionalData: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isReschedulePending: false,
            fetchingStatusEnum,
            labelMoreStaffsBtn: '',
            labelMoreContactsBtn: '',
            maxStaffToDisplay: 4,
            maxContactsToDisplay: 4,
            activityDetailData: null,
            relatedData: {},
            fetchingStatus: fetchingStatusEnum.loading,
            localizedTexts: {
                isLateLabel: window.i18n.__('En retard'),
                errorAlertLabel: window.i18n.__(
                    'Une erreur est survenue lors du chargement de cette activité. Veuillez réessayer plus tard ou contacter le support si cela se reproduit.',
                ),
                activityDetailLabel: window.i18n.__("Détails de l'activité"),
                descriptionListLabel: {
                    priority: window.i18n.__('Priorité'),
                    label: window.i18n.__('Label'),
                    date: window.i18n.__('Date'),
                    contacts: window.i18n.__('Contact(s)'),
                    description: window.i18n.__('Description'),
                    related: window.i18n.__('Éléments liés'),
                    phoneCall: {
                        duration: window.i18n.__("Temps d'appel"),
                        result: window.i18n.__("Résultat d'appel"),
                        description: window.i18n.__('Note'),
                    },
                },
            },
            headerActions: [],
            isStatusPending: false,
            isPriorityPending: false,
            batchResponse: [],
            isVisible: false,
            controller: new AbortController(),
            owner: null,
            isPopoverAssigneeDisplayed: false,
            contactsTriggerRefEl: null,
            staffsTriggerRefEl: null,
        };
    },
    computed: {
        ...userMapGetters(['getPermissions']),
        panelTitle() {
            if (this.fetchingStatus === this.fetchingStatusEnum.success) {
                switch (this.activityDetailData?.type) {
                    case this.ACTIVITY_TYPES.TASK:
                        return this.$l10n.__('Tâche');
                    case this.ACTIVITY_TYPES.EVENT:
                        return this.$l10n.__('Événement');
                    case this.ACTIVITY_TYPES.PHONECALL:
                        return this.$l10n.__('Appel');
                    default:
                        return '';
                }
            }
            return '';
        },
        panelIcon() {
            if (this.fetchingStatus === this.fetchingStatusEnum.success) {
                switch (this.activityDetailData?.type) {
                    case this.ACTIVITY_TYPES.TASK:
                        return 'obj-slsy-task';
                    case this.ACTIVITY_TYPES.EVENT:
                        return 'obj-slsy-calendar';
                    case this.ACTIVITY_TYPES.PHONECALL:
                        return 'obj-slsy-phone';
                    default:
                        return null;
                }
            }
            return null;
        },
        isActivityPhonecall() {
            return this.activityDetailData?.type === this.ACTIVITY_TYPES.PHONECALL;
        },
        rescheduleDatePickerValue() {
            if (this.activityDetailData) {
                return this.isActivityPhonecall
                    ? this.activityDetailData.date
                    : this.activityDetailData.due_date;
            }
            return '';
        },
        isFooterPending() {
            return this.activityDetailDisplayed?.priority?.pending || this.isStatusPending;
        },
        activityIsDone() {
            if (this.activityDetailDisplayed && this.activityDetailData) {
                switch (this.activityDetailDisplayed.type) {
                    case this.ACTIVITY_TYPES.TASK:
                        return this.activityDetailData.status === 'done';
                    case this.ACTIVITY_TYPES.PHONECALL:
                        return this.activityDetailData.result?.id !== 'scheduled';
                    case this.ACTIVITY_TYPES.EVENT:
                        return this.isDateBefore();
                }
            }
            return false;
        },
        isActivityLate() {
            return this.isDateBefore() && !this.activityIsDone;
        },
        assignedContactsFiltered() {
            if (
                !this.activityDetailData ||
                this.activityDetailData.type !== this.ACTIVITY_TYPES.EVENT
            ) {
                return [];
            }
            const internalContacts = this.activityDetailData._embed.participants;
            const allParticipants = this.activityDetailData.participants
                ?.filter(
                    (participant) =>
                        participant.type !== 'staff' &&
                        !participant.id.toString().includes('@resource.calendar.google.com'),
                )
                .sort((a, b) => a.type.localeCompare(b.type));

            return allParticipants.map((participant) => {
                const contact = internalContacts.find(
                    (item) =>
                        String(item.id) === String(participant.id) && item.hasOwnProperty('owner'),
                );
                return {
                    ...participant,
                    type: participant.type || 'contact',
                    contactInfo: contact || {},
                };
            });
        },
        assignedStaffsFiltered() {
            if (!this.activityDetailData) {
                return [];
            }

            if (this.activityDetailData.type === this.ACTIVITY_TYPES.EVENT) {
                return this.activityDetailData._embed.participants.filter(
                    (item) =>
                        //we want to check if item has owner property because we have contact and staff merged together but we only want staff and only contact has owner property
                        !item.hasOwnProperty('owner'),
                );
            } else if (this.isActivityPhonecall) {
                return [];
            } else {
                const listStaff =
                    this.activityDetailData._embed?.assigned_staffs.filter(
                        (staff) => staff.id !== this.activityDetailData._embed.owner.id,
                    ) ?? [];
                return orderStaffListByOwnerAndName(listStaff);
            }
        },
        activityTitle() {
            if (this.isActivityPhonecall) {
                return this.activityDetailData.source === 'outcoming'
                    ? this.$l10n.__('Appel sortant')
                    : this.$l10n.__('Appel entrant');
            }
            const title = this.activityDetailData?.title ?? this.activityDetailData?.label?.name;
            if (!title) {
                return this.activityDetailData.type === this.ACTIVITY_TYPES.TASK
                    ? this.$l10n.__('Tâche sans titre')
                    : this.$l10n.__('Événement sans titre');
            }
            return title;
        },
        formattedDueDate() {
            if (this.isActivityPhonecall) {
                return this.formatDate(this.activityDetailData.date);
            }
            return this.formatDate(this.activityDetailData.due_date);
        },
        shouldDisplayDateItem() {
            return (
                this.activityDetailData.due_date ||
                this.shouldDisplayRangeDate ||
                this.activityDetailData.date
            );
        },
        shouldDisplayRangeDate() {
            return this.activityDetailData.datetime_start && this.activityDetailData.datetime_end;
        },
        shouldDisplayDescriptionEventTask() {
            return this.activityDetailData.description && !this.isActivityPhonecall;
        },
        phoneCallDuration() {
            const duration = this.activityDetailData.duration;
            return moment().startOf('day').add(duration, 'seconds').format('HH:mm:ss');
        },
        canUpdateStatusOrReschedule() {
            return [this.ACTIVITY_TYPES.TASK, this.ACTIVITY_TYPES.PHONECALL].includes(
                this.activityDetailData?.type,
            );
        },
        label() {
            if (this.activityDetailData.label) {
                return {
                    ...this.activityDetailData.label,
                    color: `#${this.activityDetailData.label.color ?? '000000'}`,
                };
            }
            if (this.activityDetailData.topic) {
                return {
                    ...this.activityDetailData.topic,
                    color: this.getLabelColor(this.activityDetailData.topic),
                };
            }
            return null;
        },
        isActivityListing() {
            return this.$route.name === 'activities.crm.listing.all';
        },
        isFromDashboardCard() {
            return (
                this.$route.name === 'dashboard' &&
                document.querySelector('.dashboard__my-activities-card')
            );
        },
        isContactVisible() {
            return (
                this.assignedContactsFiltered.length > 0 &&
                this.activityDetailData.type === this.ACTIVITY_TYPES.EVENT
            );
        },
        shouldDisplayEventContacts() {
            return (
                this.isContactVisible &&
                this.isAgendaProviderNotSellsy &&
                this.activityDetailDisplayed === this.ACTIVITY_TYPES.EVENT
            );
        },
        isAgendaProviderNotSellsy() {
            return this.$store.getters['user/getUser'].agendaProvider !== 'sellsy';
        },
        isRelatedTypeDocument() {
            return this.relatedData.type === RELATED_TYPES.DOCUMENT;
        },
        isRelatedTypeOpporunity() {
            return this.relatedData.type === RELATED_TYPES.OPPORTUNITY;
        },
        isRelatedTypeCompany() {
            return this.relatedData.type === RELATED_TYPES.COMPANY;
        },
        isRelatedTypeContact() {
            return this.relatedData.type === RELATED_TYPES.CONTACT;
        },
    },
    watch: {
        'activityDetailDisplayed.id': {
            async handler() {
                if (this.activityDetailDisplayed) {
                    this.resetState();
                    await this.initPanel();
                }
            },
        },
        'activityDetailDisplayed.type': {
            async handler() {
                if (this.activityDetailDisplayed) {
                    this.resetState();
                    await this.initPanel();
                }
            },
        },
        $route() {
            this.onClosePanel();
        },
    },
    async mounted() {
        if (this.activityDetailDisplayed) {
            if (!this.$store.state.activitiesStores) {
                this.$store.registerModule('activitiesStores', ActivitiesStore);
            }
            await this.initPanel();
        }
    },
    created() {
        EventBus.on('activity-detail-panel:update-priority-pending', this.updatePriorityPending);
        EventBus.on('activity-detail-panel:update-priority-value', this.updatePriorityValue);
    },
    unmounted() {
        EventBus.off('activity-detail-panel:update-priority-pending', this.updatePriorityPending);
        EventBus.off('activity-detail-panel:update-priority-value', this.updatePriorityValue);
    },
    methods: {
        isEmpty,
        ...mapActions([
            'updateActivityDataPanel',
            'updateMetasData',
            'updateActivityDueDate',
            'updateActivitySelected',
        ]),
        setContactsTriggerRef(el) {
            this.contactsTriggerRefEl = el;
        },
        setStaffsTriggerRef(el) {
            this.staffsTriggerRefEl = el;
        },
        ...popupPanelMapActions(['open', 'feedProps']),
        isDateBefore() {
            if (this.activityDetailDisplayed && this.activityDetailData) {
                let date = null;
                switch (this.activityDetailDisplayed.type) {
                    case this.ACTIVITY_TYPES.PHONECALL:
                        date = this.activityDetailData.date;
                        break;
                    case this.ACTIVITY_TYPES.EVENT:
                        date = this.activityDetailData.datetime_end;
                        break;
                    case this.ACTIVITY_TYPES.TASK:
                    default:
                        date = this.activityDetailData.due_date;
                        break;
                }
                return moment(date).isBefore();
            }
            return false;
        },
        getStateStaff(staff) {
            return staff.status === 'blocked' ? 'deactivated' : 'default';
        },
        getAvatarColor(person) {
            return randAvatarColorFullname(`${person.firstname} ${person.lastname}`);
        },
        getInitials(person) {
            const initialFirstName = this.getFirstName(person).charAt(0);
            const initialLastName = this.getLastName(person).charAt(0);
            return `${initialFirstName}${initialLastName}`;
        },
        getFirstName(contact) {
            return contact.first_name ?? contact.firstname ?? '';
        },
        getLastName(contact) {
            return contact.last_name ?? contact.lastname ?? '';
        },
        formatDate(date) {
            const dateFormatted = this.$filters.dateFormatter(date, 'SLASHED_DATE');
            return `${dateFormatted}, ${moment(date).format('HH:mm')}`;
        },
        async initPanel() {
            try {
                this.headerActions = [];
                this.isVisible = true;
                this.fetchingStatus = this.fetchingStatusEnum.loading;
                this.metasDatas = this.$store.getters['activitiesStores/getMetas'];

                if (!this.metasDatas) {
                    const { data: metas } = await fetchActivitiesCrmMetas();
                    this.updateMetasData(metas);
                    this.metasDatas = metas;
                }
                await this.fetchMandatoryData();
                if (this.shouldFetchAdditionalData) {
                    await this.fetchAdditionalData();
                }

                this.activityDetailData.type = this.activityDetailDisplayed.type;
                const countStaffsMore = this.assignedStaffsFiltered.length - this.maxStaffToDisplay;
                const countContactsMore =
                    this.assignedContactsFiltered.length - this.maxContactsToDisplay;
                this.labelMoreStaffsBtn = this.$l10n.n__(
                    '+%s Collaborateur',
                    '+%s Collaborateurs',
                    countStaffsMore,
                    countStaffsMore,
                );
                this.labelMoreContactsBtn = this.$l10n.n__(
                    '+%s Contact',
                    '+%s Contacts',
                    countContactsMore,
                    countContactsMore,
                );
                this.initHeaderActions();

                this.owner = this.activityDetailData?._embed?.owner;

                this.updateActivityDataPanel(this.activityDetailData);
                this.fetchingStatus = this.fetchingStatusEnum.success;
            } catch (error) {
                if (error.name === 'AbortError') return;
                this.fetchingStatus = this.fetchingStatusEnum.error;
                this.activityDetailData = null;
            }
        },
        initHeaderActions() {
            if (this.activityDetailData._embed.acl.can_be_deleted) {
                this.headerActions.push({
                    icon: 'bin',
                    action: () => this.onDeleteActivity(),
                });
            }
            if (this.activityDetailData._embed.acl.can_be_updated) {
                this.headerActions.push({
                    icon: 'edit',
                    action: () => {
                        switch (this.activityDetailDisplayed.type) {
                            case this.ACTIVITY_TYPES.TASK:
                                Activities.openActivityForm(
                                    this.activityDetailData.id,
                                    null,
                                    null,
                                    this.callback,
                                    this.activityDetailDisplayed.type,
                                );
                                break;
                            case this.ACTIVITY_TYPES.EVENT:
                                Activities.openActivityForm(
                                    this.activityDetailData.provider_id
                                        ? this.activityDetailData.provider_id
                                        : this.activityDetailData.id,
                                    null,
                                    null,
                                    this.callback,
                                    this.activityDetailDisplayed.type,
                                    this.activityDetailData.provider_calendar &&
                                        this.activityDetailData.provider_calendar.id
                                        ? this.activityDetailData.provider_calendar.id
                                        : null,
                                );
                                break;
                            case this.ACTIVITY_TYPES.PHONECALL:
                                Activities.openPhoneCallForm(
                                    this.activityDetailData.id,
                                    null,
                                    null,
                                    this.callback,
                                    this.activityIsDone,
                                );
                                break;
                            default:
                                this.openLegacyForm(this.activityDetailData);
                        }
                        this.onClosePanel();
                    },
                });
            }
        },
        onClosePanel() {
            this.updateActivityDataPanel(null);
            this.updateActivitySelected(null);
            this.feedProps({
                key: 'activityDetailPanel',
                props: {
                    activityDetailDisplayed: null,
                    callback: null,
                    callbackOnDelete: null,
                    callbackOnCreate: null,
                },
            });
            this.$emit('close');
        },
        resetState() {
            this.controller.abort();
            this.controller = new AbortController();
            this.fetchingStatus = this.fetchingStatusEnum.loading;
            this.isStatusPending = false;
            this.activityDetailData = null;
            this.relatedData = {};
        },
        async fetchMandatoryData() {
            const response = await fetchActivityDetail(
                this.activityDetailDisplayed,
                this.controller.signal,
            );

            for (const [key, responseElement] of Object.entries(response)) {
                //We know that first element are always activity
                const itemIndex = Object.keys(response).indexOf(key);
                if (itemIndex === 0) {
                    this.activityDetailData = responseElement;
                    //We can't get doc with api so we use related embed
                    const relatedDoc = this.activityDetailData.related.find((item) =>
                        Object.keys(SalesDocumentsStatus).includes(item.type),
                    );
                    if (relatedDoc) {
                        const doc = this.activityDetailData._embed.related?.find(
                            (item) => item.id === relatedDoc.id,
                        );
                        this.relatedData.document = { ...doc, type: relatedDoc.type };
                        this.relatedData.type = this.relatedData.type ?? 'document';
                    }
                } else {
                    switch (key.split('.')[0]) {
                        case 'contacts':
                            this.relatedData.type = this.relatedData.type ?? 'contact';
                            this.relatedData.contact = responseElement;
                            break;
                        case 'individuals':
                        case 'companies':
                        case 'client':
                            this.relatedData.type = this.relatedData.type ?? 'company';
                            this.relatedData.company = responseElement;
                            break;
                        case 'opportunities':
                            this.relatedData.type = this.relatedData.type ?? 'opportunity';
                            this.relatedData.opportunity = responseElement;
                            break;
                    }
                }
            }
        },
        async fetchAdditionalData() {
            const requests = [];
            const keys = [];
            if (this.isRelatedTypeDocument) {
                const documentHasAlreadyContactInRelated = this.relatedData.document.related.find(
                    (related) =>
                        related.type === 'contact' &&
                        related.id === this.relatedData.document.contact_id,
                );
                if (!documentHasAlreadyContactInRelated && this.relatedData.document.contact_id) {
                    this.relatedData.document.related.push({
                        type: 'contact',
                        id: this.relatedData.document.contact_id,
                    });
                }
                this.relatedData.document.related.map((related) => {
                    const { request, key } = getRelatedBatchRequest(related);
                    requests.push(request);
                    keys.push(key);
                });
            }
            if (
                this.isRelatedTypeOpporunity &&
                (this.relatedData.opportunity.company_id ||
                    this.relatedData.opportunity.individual_id)
            ) {
                const { request, key } = getRelatedBatchRequest({
                    type: this.relatedData.opportunity.company_id ? 'company' : 'individual',
                    id:
                        this.relatedData.opportunity.company_id ??
                        this.relatedData.opportunity.individual_id,
                });
                requests.push(request);
                keys.push(key);
            }

            if (requests.length > 0) {
                const {
                    data: { responses },
                } = await ApiBatchClient.post(
                    '/batch',
                    {},
                    flatten(requests).map(JSON.stringify).join('\n'),
                );

                for (let i = 0; i < responses.length; i++) {
                    if (responses[i].code !== 200) {
                        continue;
                    }
                    const data = responses[i].response;
                    const type = keys[i].split('.')[0];
                    switch (type) {
                        case 'contacts':
                            this.relatedData.contact = data;
                            break;
                        case 'individuals':
                        case 'companies':
                            this.relatedData.company = data;
                            break;
                        case 'opportunities':
                            this.relatedData.opportunity = data;
                            break;
                        case 'estimates':
                        case 'invoices':
                        case 'credit-notes':
                        case 'orders':
                            this.relatedData.document = data;
                    }
                }
            }
        },
        updateStatusPhoneCall() {
            if (Feature.isEnabled('activity-new-creation-panel')) {
                if (!this.activityIsDone) {
                    markAsDoneWithNewForm({
                        callback: this.callback,
                        id: this.activityDetailData.id,
                        date: this.activityDetailData.date,
                        description: this.activityDetailData.description,
                    });
                } else {
                    markAsTodoWithNewForm({ id: this.activityDetailData.id }).then(() => {
                        if (this.callback) {
                            this.callback();
                        }
                        this.onClosePanel();
                    });
                }
                return;
            }
            PhoneCall.addCall(
                this.activityDetailDisplayed.related[0]?.id,
                this.activityDetailDisplayed.related[0]?.type,
                this.activityDetailDisplayed.id,
                () => {},
                !this.activityIsDone,
            );
            this.onClosePanel();
        },
        updateStatus(createNewTask = false) {
            if (!this.isActivityPhonecall || (this.isActivityPhonecall && this.activityIsDone)) {
                this.isStatusPending = true;
            }
            if (this.isActivityListing) {
                EventBus.emit('activity-detail-panel:updateStatus', {
                    activity: this.activityDetailData,
                    isFromPanel: true,
                    createNewTask,
                });
            } else {
                if (this.isActivityPhonecall) {
                    this.updateStatusPhoneCall();
                } else {
                    updateActivity(
                        this.activityDetailDisplayed.type,
                        this.activityDetailDisplayed.id,
                        {
                            status: this.activityIsDone
                                ? ACTIVITY_STATUS.TODO
                                : ACTIVITY_STATUS.DONE,
                        },
                    )
                        .then(() => {
                            const text =
                                this.activityIsDone !== 'done'
                                    ? this.$l10n.__("L'activité a été marquée comme terminée")
                                    : this.$l10n.__("L'activité a été marquée comme à faire");
                            this.$pushToast({
                                type: 'success',
                                icon: true,
                                close: true,
                                text: text,
                            });
                            if (!createNewTask) {
                                this.onClosePanel();
                            } else {
                                const relatedType = this.relatedData.type;
                                Activities.openActivityForm(
                                    null,
                                    this.relatedData[relatedType]?.id ?? null,
                                    this.relatedData[relatedType]?.type ?? null,
                                    this.callbackOnCreate,
                                    'task',
                                );
                            }

                            if (this.callback) {
                                this.callback();
                            }
                        })
                        .catch(() => {
                            this.$pushToast({
                                type: 'error',
                                icon: true,
                                close: true,
                                text: this.$t.__(
                                    'activities.detailPanel.ActivityDetailPanel.errorUpdateStatus',
                                ),
                            });
                        });
                }
            }
        },
        updateDueDate(rescheduledDate) {
            if (this.isActivityListing || this.isFromDashboardCard) {
                EventBus.emit('activities-listing-table:updateDueDate', {
                    activity: this.activityDetailData,
                    rescheduledDate,
                });
            } else {
                this.isReschedulePending = true;
                this.updateActivityDueDate({ activity: this.activityDetailData, rescheduledDate })
                    .then(() => {
                        this.$pushToast({
                            type: 'success',
                            icon: true,
                            close: true,
                            text: this.$t.__('activities.common.rescheduleSuccess'),
                        });
                        this.initPanel();
                        if (this.callback) {
                            this.callback();
                        }
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            close: true,
                            text: this.$t.__(
                                'activities.detailPanel.ActivityDetailPanel.errorUpdateDueDate',
                            ),
                        });
                    })
                    .finally(() => {
                        this.isReschedulePending = false;
                    });
            }
        },
        onDeleteActivity() {
            this.open({
                key: 'activityDeleteDialog',
                props: {
                    activity: this.activityDetailData,
                    callback: () => {
                        this.updateActivitySelected(null);
                        this.onClosePanel();
                        if (this.callbackOnDelete) {
                            this.callbackOnDelete();
                        }
                    },
                },
            });
        },
        updatePriorityValue(data) {
            this.activityDetailData.priority = data;
        },
        updatePriorityPending(data) {
            this.isPriorityPending = data;
        },
        updatePriority() {
            const priority = this.activityDetailData.priority;
            if (this.isActivityListing) {
                EventBus.emit('activities-listing-table:updatePriority', {
                    id: this.activityDetailData.id,
                    priority,
                    type: this.activityDetailData.type,
                });
            } else {
                const priorityValue = priority === 0 ? 1 : 0;
                this.isPriorityPending = true;
                updateActivity(this.activityDetailDisplayed.type, this.activityDetailDisplayed.id, {
                    priority: priorityValue,
                })
                    .then((res) => {
                        this.activityDetailData.priority = res.data.priority;
                    })
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            close: true,
                            text: this.$l10n.__(
                                'Une erreur est survenue lors de la mise à jour de la priorité.',
                            ),
                        });
                    })
                    .finally(() => {
                        this.isPriorityPending = false;
                    });
            }
        },
        getLabelColor(topic) {
            try {
                const {
                    task: { labels },
                } = this.metasDatas;
                const labelFound = labels.find((item) => item.label === topic.name);
                return labelFound?.color ?? '#000000';
            } catch (e) {
                console.error(e);
                return '#000000';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.participants-avatar-text {
    :deep(.compass-user__name.compass-user__name--small) {
        white-space: normal;
        word-break: break-all;
    }
}

.activity-detail-panel {
    :deep(.compass-panel__header-suffix) {
        min-width: 0;
    }

    &__title {
        align-items: center;
        color: var(--colors-common-text-standard-on-color);
        display: flex;
        gap: var(--spacing-1);

        &-ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .badge-activity-status {
            height: 20px;
        }
    }

    &__general-information,
    &__related-object {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        &--title {
            display: block;
            margin: 0 0 var(--spacing-1);
        }
    }

    &__general-information {
        margin: 0 0 var(--spacing-5);

        &-phonecall {
            border-top: solid 1px var(--colors-common-border-default);
            margin: var(--spacing-3) 0 0;
            padding: var(--spacing-3) 0 0;
        }

        :deep(.compass-description-list__item) {
            min-width: 0;
        }

        :deep(.compass-description-list__item__content) {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);
            min-width: 0;
        }

        &-date {
            display: flex;
            gap: var(--spacing-0);
            .activity-detail-panel__calendar-icon-wrapper {
                align-items: center;
                display: flex;
                height: 20px;
            }
            &-range {
                display: flex;
                flex-direction: column;
            }
        }
    }
    &__general-information--assigned-staffs__button-more {
        width: fit-content;
    }
}
</style>

<style lang="scss">
#compass-popover-container {
    .activity-detail-panel__general-information--assigned-staffs__menu-more {
        min-width: 180px;
        max-width: 260px;
        z-index: 1001;
    }
    .activity-detail-panel__general-information--assigned-contacts__menu-more {
        max-width: 256.73px;
        z-index: 1001;
    }
    .compass-popover__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);
        overflow-y: auto;
        padding: var(--spacing-1);
        .menu-more__item {
            align-items: center;
            display: flex;
            gap: var(--spacing-1);
            padding: var(--spacing-1);
            word-break: break-word;
        }
    }
}
</style>
